import { loginApi } from '@/api/common/login';
import { userApi } from '@/api/common/user';
import type { SiteRouteMeta } from '@/router/types';
import type { RoleType } from '@/types/common';
import { type User } from '@/types/common/user';
import { PermRoleKey, PermissionPageKey } from '@/types/permission';
import appUtil from '@/utils/app';
import { getAccessibleFactories, setFactoryCache } from '@/utils/factory';
import { intersection } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';
import { useAppStore } from './app';
import { filterPermissionsByOdRole, isOdRoleId } from './od-permission-filter';
import { useResidentStore } from './resident';

// 工作台"通用"视角哨兵：所有"无专属工作台"的角色合并到这一项，选中时渲染默认工作台布局。
export const WB_GENERAL_VIEW = '__general__';

export const useUserStore = defineStore('user', () => {
  // 当前用户信息
  const user = ref<User>();
  // 当前用户的页面权限，记录拥有权限的页面的 key 数组
  const pagePermissions = ref<string[]>([]);

  /**
   * @description 当前用户所属角色列表
   */
  const roles = computed<string[]>(() => {
    if (!user.value) {
      return [];
    }
    return user.value.roles.map((r) => r.id);
  });

  /**
   * @description 当前用户主角色 id = 后端显式标记 primaryRoleId（user_role.is_primary=true）。
   * 用于固定身份判定（OD 裁剪等）与工作台默认视角，不随首页视角切换而变。
   * 回落 roles[0] 仅为迁移/异常兜底（正常情况后端必带 primaryRoleId）。
   */
  const primaryRoleId = computed(() => {
    return user.value?.primaryRoleId ?? user.value?.roles?.[0]?.id;
  });

  /**
   * @description 工作台首页"当前查看角色"视角——仅工作台首页使用；其余全站按并集/主角色，不引入全局视角。
   * 默认 = 主角色；用户在首页切换器切换后持久化；单角色用户恒等于主角色（首页隐藏切换器）。
   */
  const WB_VIEW_KEY = 'workbench-view-role';
  const workbenchViewRole = ref<string | undefined>(localStorage.getItem(WB_VIEW_KEY) || undefined);
  function setWorkbenchViewRole(roleId: string) {
    workbenchViewRole.value = roleId;
    localStorage.setItem(WB_VIEW_KEY, roleId);
  }
  /** 工作台首页当前生效视角：用户切过且仍有效则用之，否则回落主角色。
   * 有效 = "通用"哨兵 / 自己拥有的角色 / admin 预览(可切任意工作台)。 */
  const workbenchActiveRole = computed(() => {
    const picked = workbenchViewRole.value;
    if (picked && (picked === WB_GENERAL_VIEW || roles.value.includes('admin') || roles.value.includes(picked))) {
      return picked;
    }
    return primaryRoleId.value;
  });

  /**
   * @description 云测账号密码登录
   */
  async function loginByAccount(userName: string, password: string) {
    const resp = await loginApi.loginByAccount({ userName, password });
    if (resp.code === 0) {
      useResidentStore().reset();
      appUtil.setUToken(resp.token, resp.expire);
      updateCurrent();
    }
    return resp;
  }

  /**
   * @description 飞书登录: 经由passport验证
   */
  async function loginByFeishu(utoken: string) {
    const resp = await loginApi.loginByPassportToken({ utoken });
    if (resp.code === 0) {
      useResidentStore().reset();
      appUtil.setUToken(resp.token, resp.expire);
      updateCurrent();
    }
    return resp;
  }

  /**
   * @description 更新当前用户信息和权限信息
   */
  async function updateCurrent() {
    pagePermissions.value = [];
    const resp = await userApi.getCurrentUserInfo();
    if (resp.code === 0) {
      user.value = resp.data;
    }
    if (!user.value?.roles || user.value?.roles.length === 0) {
      user.value && (user.value.roles = [{ id: 'guest', name: '游客' }]);
    }
    const resp2 = await userApi.getPagePermissions();
    if (resp2.code === 0) {
      // OD 裁剪按主角色（R8：固定身份/安全，不随视角切换；OD 不参与复权，主角色即其唯一角色）
      const firstRoleId = primaryRoleId.value;
      if (firstRoleId === PermRoleKey.TestOd || firstRoleId === PermRoleKey.DevOd) {
        // 外包角色（测试 OD / 研发 OD）按白名单二次裁剪，白名单定义见 od-permission-filter.ts
        resp2.data = filterPermissionsByOdRole(firstRoleId, resp2.data);
      } else if (isOrganizationUser()) {
        // 组织用户权限过滤
        resp2.data = resp2.data.filter((e) => {
          // 1. 生产质量管理模块：只保留制程问题库权限
          if (e.startsWith('production')) {
            return e === PermissionPageKey.Production || e === PermissionPageKey.ProduceIssue;
          }

          // 2. 项目空间入口（注意：'project' 不是以 'project-' 开头的）
          if (e === PermissionPageKey.Project) {
            return true;
          }

          // 3. 项目空间模块：只保留质检和特定问题权限
          if (e.startsWith('project-')) {
            // 保留质检单相关权限（排除质检标准）
            if (
              e === PermissionPageKey.ProjectInspect ||
              e === PermissionPageKey.ProjectInspectionChecklist ||
              e === PermissionPageKey.ProjectInspectMachine ||
              e === PermissionPageKey.ProjectInspectPackage ||
              e === PermissionPageKey.ProjectInspectPackageAuto
            ) {
              return true;
            }

            // 保留问题模块的父级和详情（但不包括问题列表，因为那是研发问题）
            if (e === PermissionPageKey.ProjectIssue || e === PermissionPageKey.ProjectIssueDetail) {
              return true;
            }

            // 只保留来料问题、制程问题和试产问题
            if (
              e === PermissionPageKey.ProjectIssueProductionMaterial ||
              e === PermissionPageKey.ProjectIssueProductionProcess ||
              e === PermissionPageKey.ProjectIssueTrialProduction
            ) {
              return true;
            }

            // 过滤掉其他项目空间权限（如研发、TR评审、质检标准、风险问题等）
            return false;
          }

          // 过滤掉其他所有模块的权限（cloudtest、test、precision、system等）
          // 组织用户只能访问 production 和 project 模块的特定子功能
          return false;
        });
      }
      // 试产问题仅对组织用户开放，非组织用户移除该权限
      if (!isOrganizationUser()) {
        resp2.data = resp2.data.filter((e) => e !== PermissionPageKey.ProjectIssueTrialProduction);
      }

      pagePermissions.value = resp2.data;
    }

    // 为有组织的用户初始化 currentFactory
    if (user.value && isOrganizationUser()) {
      const residentStore = useResidentStore();
      const accessibleFactories = await getAccessibleFactories(user.value);

      // 如果当前工厂不在可访问列表中，设置为第一个可访问的工厂
      const currentFactoryId = residentStore.currentFactory?.id;
      const isCurrentFactoryAccessible = accessibleFactories.some((f) => f.id === currentFactoryId);

      if (!isCurrentFactoryAccessible && accessibleFactories.length > 0) {
        residentStore.currentFactory = accessibleFactories[0];
        setFactoryCache(accessibleFactories[0]);
      }
    }

    const residentStore = useResidentStore();
    await residentStore.getAllSysProject(true);
  }

  /**
   * @description 获取当前用户信息
   */
  async function getCurrent() {
    if (!user.value) {
      if (appUtil.getUToken()) {
        await updateCurrent();
      }
    }
    return user.value;
  }

  /**
   * @description 判断当前用户是否属于某些用户id
   */
  function isUserId(u: number | number[]) {
    if (!user.value) {
      return false;
    }
    if (Array.isArray(u)) {
      return u.includes(user.value.id);
    }
    return user.value.id === u;
  }

  /**
   * @description 判断当前用户是否属于某个角色
   */
  function isRole(r: string) {
    return roles.value.includes(r);
  }

  /**
   * @description 判断当前用户是否不属于某个角色
   */
  function isRoleNot(r: string) {
    return !isRole(r);
  }

  /**
   * @description 判断当前用户是否属于某个角色列表中的任意一个
   */
  function isRolesOne(list: string[]) {
    return intersection(roles.value, list).length > 0;
  }

  /**
   * @description 判断当前用户类型
   */
  function isRoleType(type: RoleType) {
    return user.value && user.value.type === type;
  }

  /**
   * @description 判断当前用户是否属于某个角色类型列表中的一个
   */
  function isRoleTypesOne(list: RoleType[]) {
    return user.value && list.includes(user.value.type);
  }

  /**
   * @description 判断当前用户是否属于组织
   */
  function isOrganizationUser() {
    return (
      user.value?.organizationIds !== undefined &&
      user.value?.organizationIds !== null &&
      user.value?.organizationIds.length > 0
    );
  }

  /**
   * @description 判断当前用户主角色是否为 OD 外包（test-od / dev-od）
   *
   * 用于 permission-guard.ts 放宽"智能重定向"触发条件——OD 外包用户没有 organizationIds，
   * 原本走不到智能重定向直接跳 403；纳入本判断后，OD 访问无权限父级路由（如工作台硬编码
   * 跳 `/project/:pkey/dashboard`）会被重定向到首个可访问子页。纯判断委托给
   * od-permission-filter.isOdRoleId，便于单测覆盖。
   */
  function isOdUser() {
    return isOdRoleId(primaryRoleId.value);
  }

  /**
   * @description 判断当前用户是否有访问页面的权限
   */
  function hasPagePermission(key: PermissionPageKey) {
    const appStore = useAppStore();
    if (!appStore.isPageKeyInSites(key)) {
      // 不在 siteRoutes 内的页面，视为公共页面都有权限
      return true;
    }
    if (pagePermissions.value.find((e) => e === key)) {
      return true;
    }
    return false;
  }

  /**
   * @description 判断当前用户是否访问路由的权限
   */
  function hasRLNPermission(to: RouteLocationNormalized) {
    for (const r of to.matched) {
      if (r.meta.roles && !isRolesOne((r.meta as unknown as SiteRouteMeta).roles)) {
        return false;
      }
      if (hasPagePermission(r.name as PermissionPageKey)) {
        continue;
      }
      return false;
    }
    return true;
  }

  /**
   * 退出登录
   *
   * 必须在清 user.value 前清空 residentStore 的会话级缓存，否则下一个账号
   * 登录后 getAllUser/getUserSysProject 等会命中 "length === 0 ? 拉新 : 用缓存"
   * 的缓存分支，拿到上一个账号的数据（如"我的项目/产品"弹窗显示旧账号项目）。
   */
  const logout = () => {
    useResidentStore().reset();
    appUtil.rmUToken();
    appUtil.goToLoginPage(true);
    user.value = null;
  };

  return {
    current: user,
    pagePermissions,
    getCurrent,
    updateCurrent,
    loginByAccount,
    loginByFeishu,
    isUserId,
    logout,
    isRole,
    isRoleNot,
    isRolesOne,
    isRoleType,
    isRoleTypesOne,
    isOrganizationUser,
    isOdUser,
    hasRLNPermission,
    primaryRoleId,
    workbenchActiveRole,
    setWorkbenchViewRole,
  };
});
